/**
 * ProposalKanbanColumn - Coluna (situação) do Kanban de Propostas
 *
 * Carrega apenas a própria página de propostas (cursor) e exibe totais no
 * cabeçalho. Sem droppable/sortable — o board é somente leitura.
 *
 * @version 1.2.0
 * RELEASE: 2026-09-26 v1.2.0 - Reutiliza a coluna-base compartilhada sem
 *          alterar cabeçalho, paginação, estados ou cartões.
 * RELEASE: 2026-08-25 v1.1.1 - Tipografia do cabeçalho idêntica ao Kanban de Contratos
 *          (MRR passa de text-[10px] para text-[11px])
 * RELEASE: 2026-08-25 v1.1.0 - Paridade com o Kanban de Contratos: coluna sem borda externa
 *          (`bg-muted/30`), cabeçalho com bolinha 2.5, contador em pílula e valor no mesmo
 *          padrão tipográfico; lista de cards passa a usar ScrollArea (`flex-1 h-0 min-h-0`)
 *          com scroll interno e guard de infinite scroll
 * RELEASE: 2026-08-25 v1.0.0 - Criação inicial (Fase 1 — Kanban de Propostas)
 */
import React from 'react';
import { ReadonlyKanbanColumn } from '@/components/kanban/ReadonlyKanbanColumn';
import { useProposalKanbanColumn, type ProposalFilters } from '@/hooks/useProposalKanbanColumn';
import type { ProposalKanbanColumnTotals } from '@/hooks/useProposalKanbanCounts';
import type { ProposalKanbanColumnDef } from './proposalKanbanColumns';
import { ProposalKanbanCard } from './ProposalKanbanCard';

interface ProposalKanbanColumnProps {
  column: ProposalKanbanColumnDef;
  filters: ProposalFilters;
  totals?: ProposalKanbanColumnTotals;
  onOpenProposal: (proposalId: string) => void;
  onOpenDeal: (dealId: string) => void;
  onActionComplete: () => void;
}

/** Formata valores monetários compactos no cabeçalho */
function formatTotal(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: value >= 100000 ? 'compact' : 'standard',
    maximumFractionDigits: value >= 100000 ? 1 : 2,
  }).format(value || 0);
}

export function ProposalKanbanColumn({
  column,
  filters,
  totals,
  onOpenProposal,
  onOpenDeal,
  onActionComplete,
}: ProposalKanbanColumnProps) {
  const { proposals, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useProposalKanbanColumn(column.status, filters);

  const count = totals?.count ?? proposals.length;
  const totalValue = totals?.value ?? 0;
  const totalMrr = totals?.mrr ?? 0;

  return (
    <ReadonlyKanbanColumn
      label={column.label}
      count={count}
      barClass={column.barClass}
      headerClass={column.headerClass}
      itemCount={proposals.length}
      isLoading={isLoading}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      emptyLabel="Nenhuma proposta"
      headerDetail={(totalValue > 0 || totalMrr > 0) ? (
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <span className="text-[11px] text-muted-foreground tabular-nums truncate">
              {formatTotal(totalValue)}
            </span>
            {totalMrr > 0 && (
              <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
                {formatTotal(totalMrr)}/mês
              </span>

            )}
          </div>
      ) : undefined}
    >
      {proposals.map((proposal) => (
        <ProposalKanbanCard
          key={proposal.id}
          proposal={proposal}
          onOpen={onOpenProposal}
          onOpenDeal={onOpenDeal}
          onActionComplete={onActionComplete}
        />
      ))}
    </ReadonlyKanbanColumn>
  );
}
